import type { AudienceFilters, DeviceChannel } from '@brushfire/types'

/** Single-select pill in the publish UI (maps to `deviceType` channel array). */
export type DeviceTypePillValue = DeviceChannel | 'both'

/** Normalizes legacy string pills and channel arrays (mirrors `@brushfire/types`). */
export function normalizeDeviceTypeValue(value: unknown): DeviceChannel[] | undefined {
  if (value == null) return undefined
  if (typeof value === 'string') {
    if (value === 'both') return ['mobile', 'web']
    if (value === 'mobile' || value === 'web') return [value]
    return undefined
  }
  if (!Array.isArray(value)) return undefined
  const channels = value.filter((v): v is DeviceChannel => v === 'mobile' || v === 'web')
  if (!channels.length) return undefined
  return [...new Set(channels)]
}

export function pillToDeviceChannels(pill: DeviceTypePillValue): DeviceChannel[] {
  if (pill === 'both') return ['mobile', 'web']
  return [pill]
}

export function deviceChannelsToPill(value: unknown): DeviceTypePillValue | undefined {
  const channels = normalizeDeviceTypeValue(value)
  if (!channels?.length) return undefined
  if (channels.length === 2) return 'both'
  return channels[0]
}

/** Coerces legacy string `deviceType` from saved drafts into a channel array. */
export function coerceAudienceFilters(
  filters: AudienceFilters | undefined | null,
): AudienceFilters {
  const base = filters ?? {}
  const deviceType = normalizeDeviceTypeValue((base as { deviceType?: unknown }).deviceType)
  const ekyc = base.ekycStatus
  const nextEkyc = ekyc === 'verified' || ekyc === 'unverified' ? ekyc : undefined
  const deviceSame =
    (deviceType?.length ?? 0) === (base.deviceType?.length ?? 0) &&
    (deviceType ?? []).every((c, i) => c === base.deviceType?.[i])
  const ekycSame = nextEkyc === (base.ekycStatus === 'all' ? undefined : base.ekycStatus) ||
    (nextEkyc === undefined && (base.ekycStatus === undefined || base.ekycStatus === 'all'))
  // Preserve reference when already canonical (store equality / AI baseline compares).
  if (
    deviceSame &&
    ekycSame &&
    base.ekycStatus !== 'all' &&
    typeof (base as { deviceType?: unknown }).deviceType !== 'string'
  ) {
    return base
  }
  const next: AudienceFilters = { ...base }
  if (deviceType) next.deviceType = deviceType
  else delete next.deviceType
  if (nextEkyc) next.ekycStatus = nextEkyc
  else delete next.ekycStatus
  return next
}
